import {
  forwardRef,
  type Ref,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import {
  animated,
  config,
  type SpringValue,
  useReducedMotion,
  useTransition,
  type UseTransitionProps,
} from '@react-spring/web';
import clsx from 'clsx';
import { useModalSheetInputIosKeyboard } from '../useModalSheetInputIosKeyboard';
import { ModalContent } from './ModalContent/ModalContent';
import { ModalOverlay } from './ModalOverlay/ModalOverlay';
import styles from './Modal.module.css';

const AnimatedDialogOverlay = animated(ModalOverlay);
const AnimatedDialogContent = animated(ModalContent);

export interface ModalProps {
  children: ReactNode;
  isOpen: boolean;
  ariaLabel: string;
  testid?: string;
  onDismiss?: () => void;
  contentClassName?: string;
  overlayClassName?: string;
  innerContentClassName?: string;
  onAnimationEnd?: () => void;
  initialFocusRef?: MutableRefObject<null | HTMLElement>;
  onRest?: UseTransitionProps['onRest'];
}

export const Modal = forwardRef(
  (props: ModalProps, ref: Ref<HTMLDivElement>) => {
    const {
      contentClassName,
      overlayClassName,
      innerContentClassName,
      isOpen,
      onDismiss,
      testid,
      ariaLabel,
      children,
      onAnimationEnd,
      initialFocusRef,
      onRest,
    } = props;

    const prefersReducedMotion = useReducedMotion();

    const transitions = useTransition(isOpen, {
      config: {
        ...config.stiff,
        bounce: 0,
        duration: 300,
      },
      from: {
        transform: `translate3d(0, 32px, 0)`,
        opacity: 0,
      },
      update: {
        opacity: 1,
      },
      enter: {
        transform: `translate3d(0, 0%, 0)`,
        opacity: 1,
      },
      leave: {
        transform: `translate3d(0, 0px, 0)`,
        opacity: 0,
      },
      immediate: Boolean(prefersReducedMotion),
      onRest,
    });

    return transitions(({ opacity, transform }, item) => {
      if (item === false) {
        return null;
      }

      const spring = { transform, opacity };

      return (
        <AnimatedDialog
          onDismiss={onDismiss}
          initialFocusRef={initialFocusRef}
          overlayClassName={overlayClassName}
          testid={testid}
          item={item}
          onAnimationEnd={onAnimationEnd}
          opacity={opacity}
          ref={ref}
          contentClassName={contentClassName}
          ariaLabel={ariaLabel}
          spring={spring}
          innerContentClassName={innerContentClassName}
          children={children}
        />
      );
    });
  }
);

type AnimatedDialogProps = {
  onDismiss?: () => void;
  initialFocusRef?: MutableRefObject<null | HTMLElement>;
  overlayClassName?: string;
  testid?: string;
  item: boolean;
  onAnimationEnd?: () => void;
  opacity: SpringValue<number>;
  ref: Ref<HTMLDivElement>;
  contentClassName?: string;
  ariaLabel: string;
  spring: {
    transform: SpringValue<string>;
    opacity: SpringValue<number>;
  };
  innerContentClassName?: string;
  children: ReactNode;
};

const AnimatedDialog = forwardRef(
  (props: AnimatedDialogProps, ref: Ref<HTMLDivElement>) => {
    const {
      onDismiss,
      initialFocusRef,
      overlayClassName,
      testid,
      item,
      onAnimationEnd,
      opacity,
      contentClassName,
      ariaLabel,
      spring,
      innerContentClassName,
      children,
    } = props;

    const { overlayRef } = useModalSheetInputIosKeyboard();

    return (
      <AnimatedDialogOverlay
        ref={(element) => {
          overlayRef.current = element;
        }}
        onDismiss={onDismiss}
        initialFocusRef={initialFocusRef}
        className={clsx(styles.overlay, overlayClassName)}
        data-testid={testid}
        isOpen={item}
        onAnimationEnd={onAnimationEnd}
        style={{
          opacity,
          transition: 'height 200ms',
        }}
      >
        <AnimatedDialogContent
          ref={ref}
          className={clsx(styles.content, contentClassName)}
          ariaLabel={ariaLabel}
          style={spring}
        >
          <div className={innerContentClassName}>{children}</div>
        </AnimatedDialogContent>
      </AnimatedDialogOverlay>
    );
  }
);
